import React, { useRef, useState } from "react";
import { FiArrowUpRight, FiShield } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sendContactEmail } from "../../emailService";

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_phone: "",
    user_email: "",
    user_project: "",
  });
  const form = useRef();

  const handleForm = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await sendContactEmail(form.current);
      toast.success("Your request has been sent successfully!", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      setFormData({
        user_name: "",
        user_phone: "",
        user_email: "",
        user_project: "",
      });
    } catch (error) {
      console.error("EmailJS submission failed:", error);
      toast.error("We couldn't send your request. Please try again.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-[#FFDB16] focus:bg-white focus:ring-4 focus:ring-[#FFDB16]/15 lg:py-2.5";

  return (
    <div id="contact-form" className="relative overflow-hidden rounded-[30px] border border-white/60 bg-white p-6 text-slate-950 shadow-2xl shadow-black/30 sm:p-8 lg:p-6">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FFDB16] via-[#239be6] to-[#643c90]" />
      <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#FFDB16]/10 blur-3xl" />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="relative">
        <p className="mb-3 text-sm font-bold uppercase tracking-[0.12em] text-[#FFDB16] lg:mb-2">
          Project enquiry
        </p>
        <h2 className="font-heading text-3xl font-extrabold leading-tight text-slate-950 lg:text-[32px]">
          Tell us about your idea
        </h2>
        <p className="mt-3 leading-7 text-slate-600 lg:mt-2 lg:leading-6">
          Share a few details and our team will get back to you shortly.
        </p>

        <form ref={form} onSubmit={handleSubmit} className="mt-6 lg:mt-4">
          <div className="grid gap-5 sm:grid-cols-2 lg:gap-4">
            <label className="block text-sm font-bold text-slate-700" htmlFor="name">
              Name
              <input
                className={inputClass}
                id="name"
                name="user_name"
                required
                type="text"
                value={formData.user_name}
                onChange={handleForm}
                placeholder="Your name"
                autoComplete="name"
              />
            </label>

            <label className="block text-sm font-bold text-slate-700" htmlFor="email">
              Email
              <input
                className={inputClass}
                id="email"
                type="email"
                required
                value={formData.user_email}
                onChange={handleForm}
                name="user_email"
                placeholder="you@company.com"
                autoComplete="email"
                pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
              />
            </label>
          </div>

          <label className="mt-5 block text-sm font-bold text-slate-700 lg:mt-4" htmlFor="phone">
            Phone number
            <input
              className={inputClass}
              id="phone"
              type="tel"
              name="user_phone"
              value={formData.user_phone}
              onChange={handleForm}
              pattern="[0-9]{10}"
              required
              title="Please enter a valid 10-digit phone number"
              placeholder="10-digit phone number"
              autoComplete="tel"
            />
          </label>

          <label className="mt-5 block text-sm font-bold text-slate-700 lg:mt-4" htmlFor="project">
            How can we help?
            <textarea
              className={`${inputClass} min-h-[120px] resize-y lg:min-h-[82px]`}
              id="project"
              name="user_project"
              value={formData.user_project}
              onChange={handleForm}
              placeholder="Tell us about your goals, timeline, or project requirements…"
              rows="4"
              required
            />
          </label>

          <div className="mt-7 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between lg:mt-4">
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <FiShield className="h-4 w-4 text-emerald-600" />
              Your information stays private.
            </p>
            <button
              className="type-button flex min-h-[52px] min-w-[180px] items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-white transition duration-300 hover:-translate-y-0.5 hover:bg-[#FFDB16] hover:text-slate-950 hover:shadow-lg disabled:cursor-wait disabled:opacity-70"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <RotatingLines
                    strokeColor="white"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="22"
                    visible
                  />
                  Sending…
                </>
              ) : (
                <>
                  Submit Request <FiArrowUpRight className="h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
